import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CheckCircle,
  Crop as CropIcon,
  IdentificationCard,
  Image,
  Plus,
  Spinner,
  Star,
  Trash,
  UploadSimple,
} from '@phosphor-icons/react';
import {
  createTemplate,
  deleteTemplate,
  listTemplates,
  setDefaultTemplate,
  updateTemplate,
  type CardTemplate,
  type CardTemplateField,
  type PIILevel,
  type YearBinding,
  type UpdateTemplatePayload,
} from '@/api/membercards';
import { uploadMedia, publicURL } from '@/api/media';
import { ImageCropDialog } from '@/components/ImageCropDialog';
import { LoadError } from '@/components/LoadError';
import { getOrgGeneral } from '@/api/orgs';
import { ApiError } from '@/api/client';
import { MemberCardTemplatesSkeleton } from '@/components/ui/member-card-skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmDialog';

const DEFAULT_FIELDS: CardTemplateField[] = [
  { kind: 'org_logo', x: 40, y: 40, font_size: 60 },
  { kind: 'member_name', x: 40, y: 220, font_size: 36, weight: 800 },
  { kind: 'fiscal_year', x: 40, y: 280, font_size: 24, weight: 600 },
  { kind: 'qr', x: 640, y: 320, font_size: 180 },
];

export function MemberCardTemplates() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const confirm = useConfirm();

  const tplQuery = useQuery({
    queryKey: ['membercard-templates', slug],
    queryFn: () => listTemplates(token, slug),
    enabled: !!slug,
  });

  const [editingID, setEditingID] = useState<string | null>(null);
  const editing = tplQuery.data?.templates.find((t) => t.id === editingID) ?? null;

  const createMut = useMutation({
    mutationFn: () =>
      createTemplate(token, slug, {
        name: t('memberCards.editor.newTemplateName'),
        is_default: (tplQuery.data?.templates.length ?? 0) === 0,
        background_kind: 'color',
        background_color: '#26272B',
        text_color: '#FFFDD8',
        accent_color: '#BEE034',
        font_heading: 'bricolage',
        font_body: 'galano',
        fields: DEFAULT_FIELDS,
        public_pii_level: 'full_name',
        show_member_id: false,
        year_binding: 'current_year',
        content_offset_y: 0,
        content_offset_x: 0,
        background_gradient_angle: 135,
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['membercard-templates', slug] });
      setEditingID(created.id);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTemplate(token, slug, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['membercard-templates', slug] });
      setEditingID(null);
    },
  });

  const setDefaultMut = useMutation({
    mutationFn: (id: string) => setDefaultTemplate(token, slug, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['membercard-templates', slug] }),
  });

  if (tplQuery.isPending) {
    return (
      <div className="mx-auto max-w-6xl p-4 sm:p-6">
        <MemberCardTemplatesSkeleton />
      </div>
    );
  }
  if (tplQuery.error) {
    return <LoadError onRetry={() => void tplQuery.refetch()} />;
  }

  const templates = tplQuery.data?.templates ?? [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('memberCards.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-prose">
            {t('memberCards.description')}
          </p>
        </div>
        <Button onClick={() => createMut.mutate()} disabled={createMut.isPending}>
          <Plus size={18} weight="bold" className="mr-1" />
          {t('memberCards.editor.newCta')}
        </Button>
      </header>

      <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
        {/* List */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {t('memberCards.templatesHeading')}
          </h2>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('memberCards.templatesEmpty')}</p>
          ) : (
            <ul className="space-y-2">
              {templates.map((tpl) => (
                <li key={tpl.id}>
                  <button
                    type="button"
                    onClick={() => setEditingID(tpl.id)}
                    className={`w-full text-left rounded-2xl border p-4 transition-shadow hover:shadow-md ${
                      editingID === tpl.id ? 'ring-2 ring-primary' : ''
                    }`}
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-block h-10 w-16 rounded-md flex-shrink-0 overflow-hidden"
                        style={thumbnailStyle(tpl)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold truncate">{tpl.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {tpl.is_default ? (
                            <>
                              <Star size={12} weight="fill" className="inline mr-1" />
                              {t('memberCards.editor.isDefault')}
                            </>
                          ) : null}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Editor pane */}
        <div>
          {editing ? (
            <TemplateEditor
              template={editing}
              slug={slug}
              token={token}
              onDeleted={() => setEditingID(null)}
              onSetDefault={() => setDefaultMut.mutate(editing.id)}
              onDelete={async () => {
                const ok = await confirm({
                  title: t('memberCards.editor.deleteConfirmTitle'),
                  body: t('memberCards.editor.deleteConfirmBody'),
                  confirmLabel: t('memberCards.editor.delete'),
                  tone: 'destructive',
                });
                if (ok) deleteMut.mutate(editing.id);
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{t('memberCards.editor.selectHint')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function thumbnailStyle(tpl: CardTemplate): React.CSSProperties {
  if (tpl.background_kind === 'image' && tpl.background_image_object_key) {
    return {
      backgroundImage: `url(${publicURL(tpl.background_image_object_key)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  if (tpl.background_kind === 'gradient' && tpl.background_gradient_color2) {
    return {
      background: `linear-gradient(${tpl.background_gradient_angle}deg, ${tpl.background_color}, ${tpl.background_gradient_color2})`,
    };
  }
  return { backgroundColor: tpl.background_color };
}

interface EditorProps {
  template: CardTemplate;
  slug: string;
  token: string | null;
  onDeleted: () => void;
  onSetDefault: () => void;
  onDelete: () => void | Promise<void>;
}

function TemplateEditor({ template, slug, token, onSetDefault, onDelete }: EditorProps) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<CardTemplate>(template);
  const [savedError, setSavedError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (draft.id !== template.id) {
    setDraft(template);
    setShowSaved(false);
  }

  useEffect(() => {
    if (!showSaved) return;
    const id = setTimeout(() => setShowSaved(false), 2500);
    return () => clearTimeout(id);
  }, [showSaved]);

  const orgQuery = useQuery({
    queryKey: ['org', slug],
    queryFn: () => getOrgGeneral(token, slug),
    enabled: !!token && !!slug,
  });
  const orgLogoKey = orgQuery.data?.logo_object_key ?? '';

  const hasLogo = draft.fields.some((f) => f.kind === 'org_logo');

  function toggleLogo() {
    if (hasLogo) {
      setDraft({ ...draft, fields: draft.fields.filter((f) => f.kind !== 'org_logo') });
    } else {
      setDraft({
        ...draft,
        fields: [{ kind: 'org_logo', x: 40, y: 40 + draft.content_offset_y, font_size: 60 }, ...draft.fields],
      });
    }
  }

  async function handleImageFile(file: File) {
    setUploadError(null);
    setUploading(true);
    try {
      const asset = await uploadMedia(token, slug, file);
      setDraft({
        ...draft,
        background_kind: 'image',
        background_image_object_key: asset.object_key,
      });
    } catch {
      setUploadError(t('memberCards.editor.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  const saveMut = useMutation({
    mutationFn: () => {
      if (draft.background_kind === 'image' && !draft.background_image_object_key) {
        return Promise.reject(Object.assign(new Error('image_required'), { code: 'image_required' }));
      }
      const payload: UpdateTemplatePayload = {
        name: draft.name,
        background_kind: draft.background_kind,
        background_color: draft.background_color,
        text_color: draft.text_color,
        accent_color: draft.accent_color,
        font_heading: draft.font_heading,
        font_body: draft.font_body,
        fields: draft.fields,
        public_pii_level: draft.public_pii_level,
        show_member_id: draft.show_member_id,
        year_binding: draft.year_binding,
        content_offset_y: draft.content_offset_y,
        content_offset_x: draft.content_offset_x,
        background_gradient_angle: draft.background_gradient_angle,
      };
      if (draft.background_kind === 'image' && draft.background_image_object_key) {
        payload.background_image_object_key = draft.background_image_object_key;
      }
      if (draft.background_gradient_color2 !== undefined) {
        payload.background_gradient_color2 = draft.background_gradient_color2;
      }
      return updateTemplate(token, slug, draft.id, payload);
    },
    onSuccess: () => {
      setSavedError(null);
      setShowSaved(true);
      void qc.invalidateQueries({ queryKey: ['membercard-templates', slug] });
    },
    onError: (e) => {
      if (e instanceof Error && (e as { code?: string }).code === 'image_required') {
        setSavedError(t('memberCards.editor.imageRequired'));
        return;
      }
      const code = e instanceof ApiError ? e.code : 'unknown';
      setSavedError(code === 'invalid_input' ? t('memberCards.editor.invalidInput') : code);
    },
  });

  const gradientAngle = draft.background_gradient_angle ?? 135;
  const gradientColor2 = draft.background_gradient_color2 ?? '#BEE034';

  return (
    <section className="rounded-2xl border overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-5 py-4 border-b">
        <h2 className="text-lg font-bold">{t('memberCards.editor.title')}</h2>
        <div className="flex gap-2">
          {!draft.is_default ? (
            <Button variant="outline" size="sm" onClick={onSetDefault}>
              <Star size={16} weight="bold" className="mr-1" />
              {t('memberCards.editor.setDefault')}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={onDelete}>
            <Trash size={16} weight="bold" className="mr-1" />
            {t('memberCards.editor.delete')}
          </Button>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5">
        {/* Template name */}
        <Field label={t('memberCards.editor.fields.name')}>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </Field>

        {/* Background */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">{t('memberCards.background.label')}</legend>

          {/* Kind tabs */}
          <div className="flex gap-2 flex-wrap">
            <BgTab
              active={draft.background_kind === 'color'}
              onClick={() => setDraft({ ...draft, background_kind: 'color' })}
            >
              {t('memberCards.background.color')}
            </BgTab>
            <BgTab
              active={draft.background_kind === 'gradient'}
              onClick={() =>
                setDraft({
                  ...draft,
                  background_kind: 'gradient',
                  background_gradient_color2: draft.background_gradient_color2 ?? draft.accent_color,
                })
              }
            >
              {t('memberCards.background.gradient')}
            </BgTab>
            <BgTab
              active={draft.background_kind === 'image'}
              onClick={() => setDraft({ ...draft, background_kind: 'image' })}
            >
              <Image size={12} className="inline mr-1" />
              {t('memberCards.background.image')}
            </BgTab>
          </div>

          {/* Color */}
          {draft.background_kind === 'color' ? (
            <ColorInput
              value={draft.background_color}
              onChange={(v) => setDraft({ ...draft, background_color: v })}
            />
          ) : null}

          {/* Gradient */}
          {draft.background_kind === 'gradient' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">
                    {t('memberCards.gradient.color1')}
                  </p>
                  <ColorInput
                    value={draft.background_color}
                    onChange={(v) => setDraft({ ...draft, background_color: v })}
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">
                    {t('memberCards.gradient.color2')}
                  </p>
                  <ColorInput
                    value={gradientColor2}
                    onChange={(v) => setDraft({ ...draft, background_gradient_color2: v })}
                  />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">
                  {t('memberCards.gradient.angle')}
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={1}
                    value={gradientAngle}
                    onChange={(e) =>
                      setDraft({ ...draft, background_gradient_angle: Number(e.target.value) })
                    }
                    className="flex-1 accent-primary"
                  />
                  <div className="flex items-center gap-1 shrink-0">
                    <input
                      type="number"
                      min={0}
                      max={360}
                      value={gradientAngle}
                      onChange={(e) => {
                        const v = Math.min(360, Math.max(0, Number(e.target.value)));
                        setDraft({ ...draft, background_gradient_angle: v });
                      }}
                      className="w-14 rounded-md border border-input bg-background px-2 py-1.5 text-sm font-mono text-right"
                    />
                    <span className="text-sm text-muted-foreground">°</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Image */}
          {draft.background_kind === 'image' ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleImageFile(file);
                  e.target.value = '';
                }}
              />
              <div
                className={`relative w-full cursor-pointer overflow-hidden rounded-xl border-2 transition-colors ${
                  dragOver
                    ? 'border-primary bg-primary/5'
                    : draft.background_image_object_key
                      ? 'border-border'
                      : 'border-dashed border-border hover:border-foreground/30'
                }`}
                style={{ aspectRatio: '856/540', maxHeight: 280 }}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file) void handleImageFile(file);
                }}
              >
                {draft.background_image_object_key ? (
                  <>
                    <img
                      src={publicURL(draft.background_image_object_key)}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    {uploading ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <Spinner size={28} className="animate-spin text-white" />
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCropOpen(true);
                      }}
                      className="absolute top-2 right-12 flex h-8 w-8 items-center justify-center rounded-pill bg-black/50 text-white transition-colors hover:bg-black/70"
                      aria-label={t('imageInput.crop')}
                      title={t('imageInput.crop')}
                    >
                      <CropIcon size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const { background_image_object_key: _, ...rest } = draft;
                        setDraft({ ...rest, background_kind: 'color' });
                      }}
                      className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-pill bg-black/50 text-white transition-colors hover:bg-black/70"
                      aria-label={t('memberCards.editor.removeImage')}
                    >
                      <Trash size={14} />
                    </button>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
                    {uploading ? (
                      <>
                        <Spinner size={28} className="animate-spin" />
                        <span className="text-sm">{t('memberCards.editor.uploading')}</span>
                      </>
                    ) : (
                      <>
                        <UploadSimple size={36} weight="regular" />
                        <div className="text-center">
                          <p className="text-sm font-medium">{t('memberCards.editor.uploadHint')}</p>
                          <p className="mt-1 text-xs opacity-70">{t('memberCards.editor.uploadFormats')}</p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {uploadError ? (
                <p className="mt-1.5 text-xs text-destructive">{uploadError}</p>
              ) : null}
              {draft.background_image_object_key && (
                <ImageCropDialog
                  open={cropOpen}
                  src={publicURL(draft.background_image_object_key)}
                  filename={draft.background_image_object_key}
                  aspect={1.586 /* ID-1 / credit-card landscape ratio */}
                  onCrop={async (file) => {
                    const asset = await uploadMedia(token, slug, file);
                    setDraft((d) => ({
                      ...d,
                      background_kind: 'image',
                      background_image_object_key: asset.object_key,
                    }));
                  }}
                  onClose={() => setCropOpen(false)}
                />
              )}
            </div>
          ) : null}
        </fieldset>

        {/* Colors + PII + year binding */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t('memberCards.editor.fields.textColor')}>
            <ColorInput value={draft.text_color} onChange={(v) => setDraft({ ...draft, text_color: v })} />
          </Field>
          <Field label={t('memberCards.editor.fields.accentColor')}>
            <ColorInput value={draft.accent_color} onChange={(v) => setDraft({ ...draft, accent_color: v })} />
          </Field>
          <Field label={t('memberCards.pii.level')}>
            <Select
              value={draft.public_pii_level}
              onValueChange={(v) => setDraft({ ...draft, public_pii_level: v as PIILevel })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_name">{t('memberCards.pii.fullName')}</SelectItem>
                <SelectItem value="first_name">{t('memberCards.pii.firstName')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('memberCards.yearBinding.label')}>
            <Select
              value={draft.year_binding}
              onValueChange={(v) => setDraft({ ...draft, year_binding: v as YearBinding })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current_year">{t('memberCards.yearBinding.current')}</SelectItem>
                <SelectItem value="frozen_at_issue">{t('memberCards.yearBinding.frozen')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Logo toggle */}
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <div>
            <p className="text-sm font-medium">{t('memberCards.editor.fields.showLogo')}</p>
            {!orgLogoKey && (
              <p className="text-xs text-muted-foreground mt-0.5">{t('memberCards.editor.noOrgLogo')}</p>
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={hasLogo}
            disabled={!orgLogoKey && !hasLogo}
            onClick={toggleLogo}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-pill border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 ${
              hasLogo ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-pill bg-white shadow-lg ring-0 transition-transform ${
                hasLogo ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Member ID toggle */}
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <p className="text-sm font-medium">{t('memberCards.editor.fields.showMemberID')}</p>
          <button
            type="button"
            role="switch"
            aria-checked={draft.show_member_id}
            onClick={() => setDraft({ ...draft, show_member_id: !draft.show_member_id })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-pill border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              draft.show_member_id ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-pill bg-white shadow-lg ring-0 transition-transform ${
                draft.show_member_id ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Content vertical position */}
        <Field label={t('memberCards.editor.fields.contentOffsetY')}>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setDraft({ ...draft, content_offset_y: Math.max(-300, draft.content_offset_y - 10) })
              }
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              aria-label={t('memberCards.editor.contentUp')}
            >
              <ArrowUp size={14} weight="bold" />
            </button>
            <input
              type="range"
              min={-300}
              max={300}
              step={10}
              value={draft.content_offset_y}
              onChange={(e) => setDraft({ ...draft, content_offset_y: Number(e.target.value) })}
              className="flex-1 accent-primary"
            />
            <button
              type="button"
              onClick={() =>
                setDraft({ ...draft, content_offset_y: Math.min(300, draft.content_offset_y + 10) })
              }
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              aria-label={t('memberCards.editor.contentDown')}
            >
              <ArrowDown size={14} weight="bold" />
            </button>
            <span className="w-12 shrink-0 text-right font-mono text-xs text-muted-foreground">
              {draft.content_offset_y > 0 ? `+${draft.content_offset_y}` : draft.content_offset_y}
            </span>
          </div>
        </Field>

        {/* Content horizontal position */}
        <Field label={t('memberCards.editor.fields.contentOffsetX')}>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setDraft({ ...draft, content_offset_x: Math.max(-300, (draft.content_offset_x ?? 0) - 10) })
              }
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              aria-label={t('memberCards.editor.contentLeft')}
            >
              <ArrowLeft size={14} weight="bold" />
            </button>
            <input
              type="range"
              min={-300}
              max={300}
              step={10}
              value={draft.content_offset_x ?? 0}
              onChange={(e) => setDraft({ ...draft, content_offset_x: Number(e.target.value) })}
              className="flex-1 accent-primary"
            />
            <button
              type="button"
              onClick={() =>
                setDraft({ ...draft, content_offset_x: Math.min(300, (draft.content_offset_x ?? 0) + 10) })
              }
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              aria-label={t('memberCards.editor.contentRight')}
            >
              <ArrowRight size={14} weight="bold" />
            </button>
            <span className="w-12 shrink-0 text-right font-mono text-xs text-muted-foreground">
              {(draft.content_offset_x ?? 0) > 0
                ? `+${draft.content_offset_x ?? 0}`
                : (draft.content_offset_x ?? 0)}
            </span>
          </div>
        </Field>

        <Preview template={draft} orgLogoKey={orgLogoKey} />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t px-5 py-4">
        {savedError ? (
          <p className="text-sm text-destructive mr-auto">{savedError}</p>
        ) : showSaved ? (
          <p className="flex items-center gap-1.5 text-sm font-medium text-primary mr-auto">
            <CheckCircle size={16} weight="fill" />
            {t('memberCards.editor.saved')}
          </p>
        ) : null}
        <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
          <CheckCircle size={18} weight="bold" className="mr-1" />
          {saveMut.isPending
            ? t('memberCards.editor.saving')
            : showSaved
              ? t('memberCards.editor.saved')
              : t('memberCards.editor.save')}
        </Button>
      </div>
    </section>
  );
}

function BgTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-pill border px-3 py-1.5 text-xs font-semibold font-heading transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="font-medium block mb-1">{label}</span>
      {children}
    </label>
  );
}

function ColorInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-9 w-12 rounded border border-input cursor-pointer"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
      />
    </div>
  );
}

function Preview({ template, orgLogoKey }: { template: CardTemplate; orgLogoKey: string }) {
  const { t } = useTranslation('common');

  const bgStyle: React.CSSProperties = {
    aspectRatio: '856 / 540',
    color: template.text_color,
    maxWidth: 480,
  };

  if (template.background_kind === 'image' && template.background_image_object_key) {
    bgStyle.backgroundImage = `url(${publicURL(template.background_image_object_key)})`;
    bgStyle.backgroundSize = 'cover';
    bgStyle.backgroundPosition = 'center';
  } else if (template.background_kind === 'gradient' && template.background_gradient_color2) {
    bgStyle.background = `linear-gradient(${template.background_gradient_angle ?? 135}deg, ${template.background_color}, ${template.background_gradient_color2})`;
  } else {
    bgStyle.backgroundColor = template.background_color;
  }

  const showLogo = template.fields.some((f) => f.kind === 'org_logo');
  const offsetY = template.content_offset_y;
  const offsetX = template.content_offset_x ?? 0;

  const previewName =
    template.public_pii_level === 'first_name'
      ? 'Anna'
      : 'Anna Andersson';
  // Sample/preview-only values shown inside the rendered card mock — not
  // user-facing copy (the card itself carries real data at runtime).
  const previewMemberId = '#2026-0042';
  const previewPaidBadge = `2026 — ${t('memberCards.publicPaid', { year: 2026 })
    .split(' ')
    .slice(0, 2)
    .join(' ')}`;

  return (
    <div>
      <p className="text-sm font-medium mb-2">{t('memberCards.editor.preview')}</p>
      <div className="rounded-2xl shadow-2xl overflow-hidden flex items-stretch" style={bgStyle}>
        <div
          className="p-6 flex-1 flex flex-col justify-end"
          style={{ transform: `translate(${offsetX}px, ${offsetY}px)` }}
        >
          {showLogo && orgLogoKey ? (
            <img
              src={publicURL(orgLogoKey)}
              alt=""
              className="mb-3 h-8 w-auto object-contain object-left"
            />
          ) : (
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">
              <IdentificationCard size={12} weight="fill" className="inline mr-1" />
              {t('memberCards.title')}
            </p>
          )}
          <p className="text-2xl font-bold leading-tight">{previewName}</p>
          {template.show_member_id ? (
            <p className="text-xs opacity-70 mt-0.5">{previewMemberId}</p>
          ) : null}
          <p
            className="text-sm mt-2 inline-block px-3 py-1 rounded-full font-semibold self-start"
            style={{ backgroundColor: template.accent_color, color: template.background_color }}
          >
            {previewPaidBadge}
          </p>
        </div>
      </div>
    </div>
  );
}
